import { omit } from 'lodash';
import { CSSProperties, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useVirtual, VirtualItem } from 'react-virtual';
import useResizeObserver from 'use-resize-observer';
import { ScrollArea } from 'components/elements';
import { filterUnique, filterWithout } from 'lib/filter';
import { defaultColumn, rowHeight, rowOverscanCount } from './Table.config';
import { StyledTable, TableBody, TableMessage } from './Table.styled';
import { getEntityId, getSelectionRangeIds } from './Table.utils';
import { TableHeader } from './TableHeader';
import { TablePlaceholderRow } from './TablePlaceholderRow';
import { TableRow } from './TableRow';
import { TableColumnConfig, TableColumnWidthMap, TableProps } from './types';

export const Table = <TEntity extends object>(props: TableProps<TEntity>) => {
  const {
    columns: baseColumns,
    defaultColumnWidths = {} as TableColumnWidthMap<TEntity>,
    entities,
    expandable = false,
    expanded = [],
    highlighted = [],
    minSize = 0,
    multiple = true,
    selectable = false,
    selected = [],
    size = 'auto',
    sortDir,
    sortKey,
    totalCount,
    onCanExpandRow,
    onColumnsChange,
    onExpansionChange,
    onGetEntityId = getEntityId,
    onRenderDetails,
    onRowDoubleClick,
    onSelectionChange,
    onSort,
  } = props;

  // Reference to the table header element
  const headerRef = useRef<HTMLDivElement>(null);

  // Retrieve viewport element reference
  const viewportElementRef = useRef<HTMLElement>(null);

  // Total size of all header cells combined
  const [headerWidth, setHeaderWidth] = useState<number>();

  // Internal state of custom column widths to apply as overrides to the original widths
  const [columnWidths, setColumnWidths] =
    useState<TableColumnWidthMap<TEntity>>(defaultColumnWidths);

  // Map of rows whose heights don't match the defaults
  const [rowHeightMap, setRowHeightMap] = useState<Record<string, number>>({});

  // Multi-selection functionality state
  const [prevRowIndex, setPrevRowIndex] = useState<number>();
  const [prevRowRange, setPrevRowRange] = useState<Array<string>>();

  // Retrieve current height of the viewport element
  const { height: viewportElementHeight } = useResizeObserver({
    box: 'border-box',
    ref: viewportElementRef,
  });

  // Build a list of table column definitions with sort options applied
  const columns = useMemo<Array<TableColumnConfig<TEntity>>>(
    () =>
      baseColumns.map(column => ({
        ...(defaultColumn as TableColumnConfig<TEntity>),
        ...column,
        sortDir: column.sortKey && column.sortKey === sortKey ? sortDir : undefined,
        width: columnWidths[column.id] ?? column.width,
      })),
    [baseColumns, columnWidths, sortDir, sortKey],
  );

  // Calculate total height of all visible rows
  const viewportHeight = useMemo(() => {
    // Use user-specified height in pixels and override the size property value
    if (size === 'auto') {
      return viewportElementHeight;
    }

    // Determine how many rows are physically visible. Use custom size if specified or fall back
    // to the number of entities passed in and display them all. Minimum size allows ensuring that
    // the table is at least N rows high.
    const visibleRows = Math.max(minSize, Math.min(size ?? entities.length, entities.length));

    // Calculate total height of rows that should be visible (in a collapsed/default state)
    return visibleRows * rowHeight;
  }, [entities.length, minSize, size, viewportElementHeight]);

  // Build styles to apply to the main viewport (scroll area)
  const viewportStyle = useMemo<CSSProperties>(
    () => ({
      flex: size === 'auto' ? 1 : undefined,
      height: size === 'auto' ? undefined : `${viewportHeight}px`,
    }),
    [size, viewportHeight],
  );

  // Determine total number of rows that the virtualized table contains. Use total entity count
  // specified by the consumer or default to the current number of entities passed into the table.
  const totalRowCount = useMemo(() => totalCount ?? entities.length, [entities.length, totalCount]);

  // Initialize row virtualization
  const { totalSize: totalBodyHeight, virtualItems } = useVirtual({
    estimateSize: useCallback(
      (index: number) => {
        // Retrieve entity from the specified index
        const entity = entities[index];
        if (!entity) {
          return rowHeight;
        }

        // Return height of the expanded entity if available or default to the starting row height
        const id = onGetEntityId(entity);
        return rowHeightMap[id] ?? rowHeight;
      },
      [entities, rowHeightMap, onGetEntityId],
    ),
    overscan: rowOverscanCount,
    parentRef: viewportElementRef,
    size: totalRowCount,
  });

  // Determine if there are any rows to render
  const hasEntities = entities.length > 0;

  /**
   * Handle header columns being resized by storing the widths internally
   *
   * @param {string} id Column identifier
   * @param {number} width Column width
   */
  const handleColumnResize = useCallback(
    (id: string, width: number) =>
      setColumnWidths(current => ({
        ...current,
        [id]: width,
      })),
    [],
  );

  /**
   * Handle expanding and collapsing row details
   *
   * @param {number} index Row index
   * @param {boolean} expanded TRUE if the row details are expanded, FALSE otherwise
   */
  const handleRowExpand = useCallback(
    (entity: TEntity, isExpanded: boolean) => {
      // Ensure expansion handler is present
      if (!onExpansionChange) {
        return;
      }

      // Generate a set of expanded entity identifiers
      const id = onGetEntityId(entity);
      const replacement = isExpanded ? expanded.concat(id) : filterWithout(expanded, [id]);

      onExpansionChange(replacement);
    },
    [expanded, onExpansionChange, onGetEntityId],
  );

  /**
   * Handle changes to individual row heights
   *
   * @param {number} index Row index
   * @param {number} height Row height
   */
  const handleRowResize = useCallback(
    (entity: TEntity, index: number, height: number) =>
      setRowHeightMap(map => {
        // Get entity identifier
        const id = onGetEntityId(entity);

        // Remove the record if row height matches the default height, or add otherwise
        return height !== rowHeight ? { ...map, [id]: height } : omit(map, id);
      }),
    [onGetEntityId],
  );

  /**
   * Handle selecting or deselecting rows
   *
   * @param {number} index Index of the (de)selected row
   * @param {boolean} select TRUE if the row is now selected, FALSE otherwise
   * @param {boolean} shiftKey TRUE if user is selecting a range of rows
   * @param {boolean} ctrlKey TRUE if individual rows are being added or removed from the selection
   */
  const handleRowSelect = useCallback(
    (index: number, select: boolean, shiftKey: boolean, ctrlKey: boolean) => {
      if (!onSelectionChange) {
        return;
      }

      // Handle single row selection
      if (!multiple) {
        // Get a list of entity ids within the calculated range
        const range = getSelectionRangeIds(entities, index, index, onGetEntityId);
        return select ? onSelectionChange(range) : onSelectionChange([]);
      }

      // Shift key not being pressed
      if (!shiftKey) {
        // Get a list of entity ids within the calculated range
        const range = getSelectionRangeIds(entities, index, index, onGetEntityId);

        if (!ctrlKey) {
          // Replace previous selection with the current range (single row) if not holding Ctrl key
          onSelectionChange(range);
        } else {
          if (select) {
            // Add row to the current selection
            const value = filterUnique(selected, range);
            onSelectionChange(value);
          } else {
            // Remove clicked row from the selection
            const value = filterWithout(selected, range);
            onSelectionChange(value);
          }
        }

        // Store index of the current row for use with shift-select
        setPrevRowIndex(index);

        // Unset range of selected rows when only selecting one row
        setPrevRowRange(undefined);
      } else {
        // Determine index of the previously clicked row
        const lastRowIndex = prevRowIndex ?? index;

        // Get a list of entity ids within the calculated range
        const range = getSelectionRangeIds(entities, index, lastRowIndex, onGetEntityId);

        // Exclude previously selected range if the user was also holding Shift button
        const selection = prevRowRange ? filterWithout(selected, prevRowRange) : selected;
        const values = filterUnique(selection, range);
        onSelectionChange(values);

        // Store current range for when user selects another range right after this
        setPrevRowRange(range);
      }
    },
    [entities, multiple, prevRowIndex, prevRowRange, selected, onGetEntityId, onSelectionChange],
  );

  /**
   * Update header position when horizontally scrolling viewport contents
   *
   * @param {Event} event Scroll event object
   */
  const handleViewportScroll = useCallback(
    (event?: UIEvent) => {
      // Ensure header component has been mounted before updating its styles
      if (!event || !headerRef.current) {
        return;
      }

      headerRef.current.style.transform = `translateX(${
        (event.target as HTMLElement).scrollLeft * -1
      }px)`;
    },
    [headerRef],
  );

  /**
   * Render individual table rows
   *
   * @param {*} entity Row entity
   * @param {number} index Row index
   * @param {CSSProperties} style Row styles
   */
  const renderRow = (entity: TEntity, index: number, style: CSSProperties) => {
    // Extract entity identifier
    const id = onGetEntityId(entity);

    // Determine if the row is expanded, selected or highlighted
    const isExpanded = expanded.includes(id);
    const isHighlighted = selected.includes(id) || highlighted.includes(id);
    const isSelected = selected.includes(id);

    return (
      <TableRow<TEntity>
        columns={columns}
        entity={entity}
        expandable={expandable}
        expanded={isExpanded}
        highlighted={isHighlighted}
        index={index}
        key={id}
        selectable={selectable}
        selected={isSelected}
        style={style}
        onCanExpand={onCanExpandRow}
        onDetailsRender={onRenderDetails}
        onDetailsToggle={handleRowExpand}
        onDoubleClick={onRowDoubleClick}
        onResize={handleRowResize}
        onSelect={handleRowSelect}
      />
    );
  };

  /**
   * Render individual virtualized table rows
   *
   * @param {VirtualItem} item Virtual row being rendered
   */
  const renderVirtualItem = (item: VirtualItem) => {
    // Build CSS styles of the row being rendered
    const style: CSSProperties = {
      height: `${item.size}px`,
      transform: `translateY(${item.start}px)`,
      width: headerWidth ? `${headerWidth}px` : undefined,
    };

    // Ensure the row at the specified index exists before rendering it
    const entity = entities[item.index];
    if (!entity) {
      return (
        <TablePlaceholderRow columns={columns} key={`placeholder:${item.index}`} style={style} />
      );
    }

    return renderRow(entity, item.index, style);
  };

  /**
   * Render table body content
   */
  const renderBody = () => {
    // Render no results message if there are no entities available
    if (!hasEntities) {
      return <TableMessage>No rows to display</TableMessage>;
    }

    return (
      <ScrollArea
        flex={true}
        ref={viewportElementRef}
        style={viewportStyle}
        onScroll={handleViewportScroll}
      >
        <TableBody role="rowgroup" style={{ height: `${totalBodyHeight}px` }}>
          {virtualItems.map(renderVirtualItem)}
        </TableBody>
      </ScrollArea>
    );
  };

  // Notify consumers about column widths changes
  useEffect(
    () => onColumnsChange && onColumnsChange(columnWidths),
    [columnWidths, onColumnsChange],
  );

  // Reset header position when list of entities becomes empty to avoid header being out of
  // position when list of entities repopulates and body scroll is reset
  useEffect(() => {
    // Ensure header component has been mounted before updating its styles
    if (!headerRef.current || entities.length !== 0) {
      return;
    }

    headerRef.current.style.transform = 'unset';
  }, [entities]);

  return (
    <StyledTable role="table">
      <TableHeader
        columns={columns}
        expandable={expandable}
        ref={headerRef}
        onColumnResize={handleColumnResize}
        onResize={setHeaderWidth}
        onSort={onSort}
      />
      {renderBody()}
    </StyledTable>
  );
};
